import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Calendar, User, Share2, TrendingUp } from 'lucide-react';
import { blogPostsData } from '../data/blogPostsData';
import InquiryModal from '../components/InquiryModal';

const BlogDetail = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  const handleButtonClick = (context) => {
    setButtonContext(context);
    setIsModalOpen(true);
  };
  
  // Use imported blog data
  const blogPosts = blogPostsData;
  
  const post = blogPosts.find(p => p.id === parseInt(id));
  
  if (!post) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#6A0572] mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blogs">
            <Button className="bg-[#6A0572] hover:bg-[#AB2346] text-white">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Back Button */}
        <div className="absolute top-8 left-4 md:left-8 z-10">
          <Link to="/blogs">
            <Button variant="outline" className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Button>
          </Link>
        </div>
        
        {/* Title & Meta */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#6A0572] to-[#AB2346] text-white text-sm font-bold rounded-full uppercase tracking-wider">
                {post.category}
              </span>
              {post.featured && (
                <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#6A0572] text-sm font-black rounded-full uppercase tracking-wider">
                  Featured
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-[#6A0572]" />
                </div>
                <div>
                  <p className="font-bold text-white">{post.author}</p>
                  <p className="text-sm text-white/70">{post.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-12 pb-8 border-b border-gray-200">
            {post.tags.map((tag, idx) => (
              <span key={idx} className="text-sm font-semibold px-4 py-2 bg-[#FFD700]/20 text-[#AB2346] rounded-full">
                #{tag}
              </span>
            ))}
          </div>
          
          {/* Blog Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-black prose-headings:text-[#6A0572] prose-headings:tracking-tight
              prose-h2:text-4xl prose-h2:mt-16 prose-h2:mb-8 prose-h2:leading-tight
              prose-h3:text-3xl prose-h3:mt-12 prose-h3:mb-6 prose-h3:leading-snug  
              prose-h4:text-2xl prose-h4:mt-8 prose-h4:mb-4 prose-h4:font-bold
              prose-p:text-gray-700 prose-p:text-lg prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-[#AB2346] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#6A0572] prose-strong:font-bold
              prose-ul:my-8 prose-ul:space-y-3 
              prose-li:text-gray-700 prose-li:text-lg prose-li:leading-relaxed
              prose-ol:my-8 prose-ol:space-y-3"
            dangerouslySetInnerHTML={{ __html: (post.content || `
              <h2>Coming Soon</h2>
              <p>This blog post is currently being written. Check back soon for the complete article!</p>
              <p>${post.excerpt}</p>
            `).replace(/^\s+/gm, '').trim() }}
          />
          
          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Share this article</p>
                <div className="flex gap-3">
                  <Button size="sm" variant="outline" className="border-[#6A0572] text-[#6A0572] hover:bg-[#6A0572] hover:text-white">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#6A0572] via-[#8B1587] to-[#6A0572] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFD700] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#AB2346] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block mb-6">
            <TrendingUp className="h-12 w-12 text-[#FFD700] mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let our expert IP attorneys help you secure your competitive advantage and unlock growth opportunities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => handleButtonClick(`Start Your Protection - Blog: ${post.title}`)}
              className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#e5a200] hover:to-[#FF8C00] text-[#6A0572] font-black px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-[0_20px_60px_rgba(255,215,0,0.4)] transition-all duration-300 hover:scale-105"
            >
              Start Your Protection
            </Button>
            <Link to="/blogs">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#6A0572] font-bold px-10 py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105"
              >
                Read More Articles
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={buttonContext}
      />
    </div>
  );
};

export default BlogDetail;
